import React from 'react';
import { Card, Button, Typography, Spin, Alert, Space } from 'antd';
import { RobotOutlined, ReloadOutlined, BulbOutlined } from '@ant-design/icons';
import { useAIStream } from '../hooks/useAIStream';

const { Text, Paragraph } = Typography;

interface AIInsightsCardProps {
  cnpj: string;
  tipo: 'resumo' | 'comercial';
  titulo?: string;
  autoGenerate?: boolean;
}

export const AIInsightsCard: React.FC<AIInsightsCardProps> = ({ 
  cnpj, 
  tipo,
  titulo,
  autoGenerate = false 
}) => {
  const { content, isLoading, error, generate, reset } = useAIStream();
  const [hasGenerated, setHasGenerated] = React.useState(false);

  React.useEffect(() => {
    if (autoGenerate && cnpj && !hasGenerated) {
      generate(cnpj, tipo);
      setHasGenerated(true);
    }
  }, [autoGenerate, cnpj, tipo, generate, hasGenerated]);

  React.useEffect(() => {
    // Reset quando CNPJ mudar
    setHasGenerated(false);
    reset();
  }, [cnpj, reset]);

  const handleGenerate = () => {
    reset();
    generate(cnpj, tipo);
  };

  const cardTitle = titulo || (tipo === 'comercial' ? 'Inteligência Comercial (Beta)' : 'Resumo da Empresa');
  const icon = tipo === 'comercial' ? <BulbOutlined /> : <RobotOutlined />;

  return (
    <Card
      title={
        <Space>
          {icon}
          <span>{cardTitle}</span>
          <Text type="secondary" style={{ fontSize: 12, fontWeight: 'normal' }}>
            ✨ Gerado por IA
          </Text>
        </Space>
      }
      extra={
        <Button
          icon={<ReloadOutlined spin={isLoading} />}
          onClick={handleGenerate}
          loading={isLoading}
          size="small"
          type={content ? 'default' : 'primary'}
        >
          {content ? 'Regenerar' : 'Gerar'}
        </Button>
      }
      style={{ marginBottom: 16 }}
      styles={{ body: { minHeight: 120 } }}
    >
      {error && (
        <Alert
          message="Erro ao gerar análise"
          description={error}
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      {isLoading && !content && (
        <div style={{ textAlign: 'center', padding: 24 }}>
          <Spin size="large" />
          <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
            Analisando dados da empresa...
          </Text>
        </div>
      )}

      {content && (
        <div className="ai-content">
          <Paragraph style={{ whiteSpace: 'pre-wrap', marginBottom: 0 }}>
            {content}
            {isLoading && <Spin size="small" style={{ marginLeft: 8 }} />}
          </Paragraph>
        </div>
      )}

      {!content && !isLoading && !error && (
        <div style={{ textAlign: 'center', padding: 24, color: '#999' }}>
          <RobotOutlined style={{ fontSize: 32, marginBottom: 8 }} />
          <br />
          <Text type="secondary">
            Clique em "Gerar" para obter {tipo === 'comercial' ? 'inteligência comercial' : 'um resumo'} desta empresa.
          </Text>
        </div>
      )}
    </Card>
  );
};

export default AIInsightsCard;
